import { Link } from "react-router-dom";
import { useWallet } from "@/hooks/use-wallet";
import { useTransactions, type TransactionKind } from "@/hooks/use-transactions";
import { formatTokens, relativeTime } from "@/lib/utils";

export function WalletPage() {
  const wallet = useWallet();
  const txns = useTransactions();

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <Link to="/" className="text-sm text-neutral-500 hover:underline">
        ← Home
      </Link>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">Wallet</h1>

      {/* Balance summary */}
      <section className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <BalanceCard
          label="Available"
          value={wallet.data?.available ?? 0}
          loading={wallet.isLoading}
          highlight
        />
        <BalanceCard
          label="Held"
          value={wallet.data?.held ?? 0}
          loading={wallet.isLoading}
          subtitle="reserved for active jobs"
        />
        <BalanceCard
          label="Total"
          value={wallet.data?.total ?? 0}
          loading={wallet.isLoading}
        />
      </section>

      {/* Per-cohort breakdown */}
      {wallet.data && wallet.data.by_cohort.length > 0 ? (
        <section className="mt-10">
          <h2 className="text-sm font-medium text-neutral-500">
            Expiring balances
          </h2>
          <ul className="mt-3 divide-y divide-neutral-200 overflow-hidden rounded-xl border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
            {wallet.data.by_cohort.map((cohort) => (
              <li
                key={cohort.id}
                className="flex items-center justify-between bg-white px-5 py-3 text-sm dark:bg-neutral-950"
              >
                <div>
                  <span className="font-medium capitalize">{cohort.kind}</span>
                  <span className="ml-2 text-xs text-neutral-500">
                    expires {new Date(cohort.expires_at * 1000).toLocaleDateString()}
                  </span>
                </div>
                <span className="font-medium">
                  {formatTokens(cohort.amount_remaining)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Transaction history */}
      <section className="mt-10">
        <h2 className="text-sm font-medium text-neutral-500">
          Transaction history
        </h2>
        {txns.isLoading ? (
          <div className="mt-3 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-lg bg-neutral-100 dark:bg-neutral-900"
              />
            ))}
          </div>
        ) : (txns.data?.items.length ?? 0) === 0 ? (
          <p className="mt-4 text-sm text-neutral-500">No activity yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-neutral-200 overflow-hidden rounded-xl border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
            {txns.data!.items.map((tx) => {
              const delta = tx.available_delta;
              const sign = delta > 0 ? "+" : delta < 0 ? "−" : "";
              const magnitude = Math.abs(delta);
              return (
                <li
                  key={tx.id}
                  className="flex items-center justify-between bg-white px-5 py-3 text-sm dark:bg-neutral-950"
                >
                  <div className="min-w-0">
                    <p className="font-medium capitalize">
                      {describeKind(tx.kind)}
                    </p>
                    <p className="truncate text-xs text-neutral-500">
                      {tx.job_id ? `Job ${tx.job_id}` : "System"} ·{" "}
                      {relativeTime(tx.created_at)}
                    </p>
                  </div>
                  {magnitude > 0 ? (
                    <span
                      className={
                        delta > 0
                          ? "font-medium text-emerald-600 dark:text-emerald-400"
                          : "font-medium text-neutral-900 dark:text-neutral-200"
                      }
                    >
                      {sign}
                      {formatTokens(magnitude)}
                    </span>
                  ) : (
                    <span className="text-xs text-neutral-400">—</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function BalanceCard({
  label,
  value,
  loading,
  highlight,
  subtitle,
}: {
  label: string;
  value: number;
  loading: boolean;
  highlight?: boolean;
  subtitle?: string;
}) {
  return (
    <div
      className={
        highlight
          ? "rounded-xl bg-emerald-500 p-6 text-white"
          : "rounded-xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950"
      }
    >
      <p
        className={
          highlight ? "text-xs uppercase tracking-wide text-emerald-50" : "text-xs uppercase tracking-wide text-neutral-500"
        }
      >
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold">
        {loading ? "…" : formatTokens(value)}
      </p>
      <p
        className={
          highlight ? "mt-1 text-xs text-emerald-50/90" : "mt-1 text-xs text-neutral-500"
        }
      >
        {subtitle ?? "tokens"}
      </p>
    </div>
  );
}

function describeKind(kind: TransactionKind): string {
  switch (kind) {
    case "credit":
      return "credit granted";
    case "hold":
      return "hold placed";
    case "capture":
      return "captured";
    case "release":
      return "released";
    case "refund":
      return "refunded";
    case "expire":
      return "expired";
    case "adjustment":
      return "adjustment";
    case "debit":
      return "debit";
    default:
      return kind;
  }
}
